import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { Col } from "react-bootstrap";

import _ from "lodash";
import cx from "classnames";

import Button from "src/components/button";
import TinyMCE from "react-tinymce/lib/components/TinyMCE";
import { DEFAULT_FONTS } from "src/constants/fonts";
import contentStyle from "./content.scss";

import { editCourse, fetchDraftCourse, invalidatePublishCourse, publishCourse, updateCourse, updateCourseProperty, updateValidationErrorsForCourse, validateCourse } from "src/redux/actions/coaching/courses";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import {
  getCoursePreferences,
  prepareCourseRequest,
  validateCourseRequest,
} from "./helper";

import { EditCourseThumbnail } from "./thumbnail";
import TextareaAutosize from "react-autosize-textarea";
import { Course, CourseValidationError } from "src/types/coaching/courses";
import { Banner } from "src/types/common";
import { BannerView } from "src/components/banner";
import { AssessmentCard, LessonCard } from "./components/cards";
import { CourseMenuBar } from "./components/menu";
import Reveal from "src/components/animation";
import { CourseLearners } from "./components/learners";
import { SelectSkillsDialog } from "./components/selectSkills";
import Thumbnail from "src/components/thumbnail";
import { AddLearnersDialog } from "./components/learners/add";

import { PassPercentage } from "./components/percentage";
import { CourseContext, CourseContextProps } from "./context";
import EmptyMessage from "src/components/emptyMessage";
import InfiniteScroll from "src/components/infiniteScroll";

import styles from "./edit.module.scss";
import { LoadingCoursePage } from "src/pages/coaching/components/course/view";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";
import Text from "src/components/text";

export const EditCoursePage: React.FC<object> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id;

  const account = useSelector((state: any) => _.get(state, "accounts.item"));
  const customfontStyle = _.get("account", "theme.customStyleSheet", "");

  const nameInputRef = React.useRef<any>(null);
  const descriptionInputRef = React.useRef<any>(null);

  const persistedCourse: Course = useSelector(
    (state: any) => _.get(state.courses, `course[${id}].item`) as Course,
  );

  const loading = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].loading`);
  });

  const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });

  const courseErrorCode = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].error.errors[0].code`);
  });

  const { publishing, published } = useSelector((state: any) => {
    return {
      publishing: _.get(state.courses, 'publish.publishing'),
      published: _.get(state.courses, 'publish.published')
    }
  });

  const name = useMemo(() => {
    return _.get(course, "name") || "";
  }, [course]);

  const description = useMemo(() => {
    return _.get(course, "description") || "";
  }, [course]);

  const banner = useMemo(() => {
    return _.get(course, "banner");
  }, [course]);

  const passPercentage = useMemo(() => {
    return _.get(course, "passPercentage") || 0;
  }, [course]);


  const { loadingLearners, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'courses.learners');
    const loadingLearners = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loadingLearners: loadingLearners,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const [editThumbnail, setEditThumbnail] = useState(false);
  const [showSkillSlider, setShowSkillSlider] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const [courseContext, setCourseContext] = useState<CourseContextProps>({
    id: id,
    courseContent: undefined,
    preferences: getCoursePreferences(),
    updatePreferences: () => { },
    gotoError: () => { },
  });

  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  })

  const fonts = useMemo(() => {
    let fonts = [...DEFAULT_FONTS];
    const customFonts = _.get(account, "theme.fonts", []);

    if (customFonts && customFonts.length > 0) {
      fonts = [...customFonts, ...fonts];
    }

    return fonts
      .map((font) => {
        return `${font.name}=${font.fontFamily}`;
      })
      .join(";");
  }, [account]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "description" });
  });

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updateCourseProperty({
        course: course.id!,
        property,
        value,
      }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleEditorSetup = useCallback((editor: any) => {
    descriptionInputRef.current = editor;
  }, [])

  const handleDescriptionChange = (e: any) => {
    const content = e.target.getContent();
    handlePropertyChange("description", content);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const handlePercentageChange = (e: number) => {
    handlePropertyChange("passPercentage", e);
  }

  const handleSave = () => {
    const validationErrors = validateCourseRequest(course);

    if (_.size(validationErrors) > 0) {
      dispatch(updateValidationErrorsForCourse({
        course: course.id!,
        errors: validationErrors,
      }));
    }

    const request = prepareCourseRequest(persistedCourse, course);

    dispatch(
      updateCourse({
        id: course.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {
    const validationErrors = validateCourseRequest(course);

    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForCourse({
          course: course.id!,
          errors: validationErrors,
        }),
      );

      return;
    }

    dispatch(publishCourse(course.id!));
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  };

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handleContentClicked = (index) => {
    navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
  };

  const handleSkillRemove = (skill) => {
    const items = course.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const getIndexForLesson = (lesson) => {
    const lessons = course.contents?.filter(
      (content) => content.entityType === "lesson",
    );
    return _.findIndex(lessons, (content) => content.uuid === lesson);
  };

  const handleGoToError = (error?: CourseValidationError) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        validationError: error,
      };
    });
  };

  const handleMoreLearners = () => {
    if (nextPage) {
      const request = {
        course: id,
        status: "ALL",
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  }

  useEffect(() => {
    if (id) {
      dispatch(fetchDraftCourse(id));

      const courseLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(courseLearnerRequest));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: id,
        gotoError: handleGoToError,
      };
    });
  }, [id]);

  useEffect(() => {
    if (persistedCourse && persistedCourse.id !== course?.id) {
      dispatch(editCourse(persistedCourse));
      dispatch(validateCourse(persistedCourse.id!));
    }
  }, [persistedCourse, course]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;
      const property = courseContext.validationError.property;

      switch (type) {
        case "course": {
          navigate(`/coaching/manage/courses/draft/${id}`);
          if (property === "name") {
            setTimeout(() => {
              nameInputRef.current?.focus();
            }, 1000);
          } else if (property === "description") {
            setTimeout(() => {
              descriptionInputRef.current?.focus();
            }, 1000);
          } else if (property === "banner") {
            setEditThumbnail(true);
          } else if (property === "contents") {
            navigate(`/coaching/manage/courses/draft/${id}/contents`);
          } else if (property === "skills") {
            setShowSkillSlider(true);
          }
          courseContext.gotoError(undefined);
          break;
        }

        case 'assessment': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          break;
        }

        case 'lesson': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          courseContext.gotoError(undefined);
          break;
        }

        default: {
          break;
        }
      }
    }
  }, [courseContext.validationError]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if(courseErrorCode === 10007){
    return <CourseNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }}/>
  }

  if ((loading && course?.id != id) || !course) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.back}
              onClick={() => {
                navigate(`/coaching/manage`);
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>courses</span>
            </div>
          </div>
        </div>
        <div className={styles.editContainer}>
          <div className={styles.left}>
            <LoadingCoursePage />
          </div>
          <div className={styles.right}></div>
        </div>
      </div>
    );
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <div key={course.id} className={styles.container}>
        {!isPublished && (
          <div className={styles.header}>
            <div className={styles.backContainer}>
              <div
                className={styles.back}
                onClick={() => {
                  navigate(`/coaching/manage`);
                }}
              >
                <i className="paperflite-left-arrow"></i>
                <span>courses</span>
              </div>
            </div>
            <div className={styles.menubar}>
              <CourseMenuBar
                course={course}
                menuState={menuState}
                onMenuStateChange={handleMenuStateChange}
              />
            </div>
          </div>
        )}

        {editTransitions((transitionStyles, item) =>
          item && (
            <animated.div
              className={styles.editContainer}
              style={transitionStyles}>
              <div className={styles.left}>
                <div className={styles.details}>
                  <div className={cx(styles.field, styles.name)}>
                    <TextareaAutosize
                      className={styles.input}
                      ref={nameInputRef}
                      placeholder="Enter course name..."
                      rows={1}
                      maxRows={10}
                      value={name}
                      data-testid="course-name"
                      onChange={handleNameChange}
                      onPointerEnterCapture={() => { }}
                      onPointerLeaveCapture={() => { }}
                    />
                    <Reveal show={nameError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{nameError?.message}</span>
                      </div>
                    </Reveal>
                  </div>

                  <div
                    className={cx(styles.field, styles.thumbnail)}
                    onClick={() => {
                      setEditThumbnail(true);
                    }}
                  >
                    <div className={styles.inner}>
                      <div className={styles.overlay}>
                        <i className="paperflite-edit"></i>
                      </div>
                      <BannerView
                        banner={banner as any}
                        defaultBanner="/images/assets/default_asset.jpg"
                      />
                    </div>
                    <Reveal show={bannerError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{bannerError?.message}</span>
                      </div>
                    </Reveal>
                  </div>

                  <div className={cx(styles.field, styles.description)}>
                    <div className={styles.label}>Description</div>
                    <TinyMCE
                      className={styles.tinyEditor}
                      content={description}
                      id={`description-${id}`}
                      data-testid="course-description"
                      config={{
                        placeholder: "Enter your course description here...",
                        inline: true,
                        branding: false,
                        statusbar: false,
                        contextmenu: false,
                        theme: "silver",
                        plugins:
                          "code, autoresize, noneditable, variable, paste, link, autolink, lists",
                        menubar: false,
                        toolbar:
                          "undo redo | bold italic | alignleft aligncenter alignright | fontselect | fontsizeselect | forecolor backcolor | code | link | cta | variables",
                        fontsize_formats:
                          "5px 5.5px 6.5px 7.5px 8px 9px 10px 10.5px 11px 12px 14px 16px 18px 20px 22px 24px 26px 28px 36px 48px 72px",
                        autoresize_bottom_margin: 0,
                        autoresize_min_height: 100,
                        paste_retain_style_properties: "color",
                        resize: false,
                        min_height: 100,
                        content_css: [
                          "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i",
                          contentStyle,
                          customfontStyle,
                        ],
                        font_formats: fonts,
                        default_link_target: "_blank",
                        browser_spellcheck: true,
                        convert_urls: false,
                        relative_urls: false,
                        remove_script_host: false,
                        setup: handleEditorSetup,
                      }}
                      onChange={handleDescriptionChange}
                    />
                    <Reveal show={descriptionError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{descriptionError?.message}</span>
                      </div>
                    </Reveal>
                  </div>

                  <div className={cx(styles.field, styles.contents)}>
                    {_.size(_.get(course, "contents")) > 0 && (
                      <div className={styles.label}>
                        Course Content
                        <Button
                          variant="tertiary"
                          icon="paperflite-edit"
                          onClick={() =>
                            navigate(`/coaching/manage/courses/draft/${id}/contents`)
                          }
                        >
                          edit content structure
                        </Button>
                      </div>
                    )}

                    {_.size(_.get(course, "contents")) == 0 && (
                      <>
                        <div className={styles.label}>Course Structure</div>
                        <EmptyCourseContent course={course.id!} />
                      </>
                    )}

                    <div className={styles.contentsList}>
                      {course.contents?.map((content, index) => {
                        const type = _.get(content, "entityType");
                        if (type === "lesson") {
                          const lessonIndex = getIndexForLesson(content.uuid);
                          return (
                            <LessonCard
                              key={index}
                              lessonIndex={lessonIndex}
                              lesson={content}
                              index={index}
                              active={false}
                              readonly
                              onClick={handleContentClicked}
                            />
                          );
                        } else if (type === "assessment") {
                          return (
                            <AssessmentCard
                              key={index}
                              assessment={content}
                              index={index}
                              active={false}
                              readonly
                              onClick={handleContentClicked}
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.right}>
                <div className={styles.skillsContainer}>
                  <p className={styles.heading}>
                    Skills, learner will get ({course.skills?.length || 0})
                  </p>
                  <div className={styles.skills}>
                    <div
                      className={styles.addSkillButton}
                      onClick={() => onSkillClicked()}
                    >
                      <i className={cx(styles.icon, "paperflite-add")}></i>
                    </div>

                    {(course.skills || []).map((skill: any) => {
                      return (
                        <div key={skill.id} className={styles.skill}>
                          <div className={styles.name}>{skill.name}</div>
                          <i
                            className={cx("paperflite-close", styles.closeIcon)}
                            onClick={() => handleSkillRemove(skill)}
                          ></i>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.courseLearnersContainer}>
                  <CourseLearners id={course.id!} />
                </div>

                {course.id && (
                  <div className={styles.passPercentageContainer}>
                    <PassPercentage value={passPercentage} handlePercentageChange={handlePercentageChange} />
                  </div>
                )}
              </div>
            </animated.div>))}

        {editThumbnail && (
          <EditCourseThumbnail
            show={editThumbnail}
            banner={course?.banner || null}
            onFinish={handleThumbnailChange}
            onClose={() => setEditThumbnail(false)}
          />
        )}

        {showSkillSlider && (
          <SelectSkillsDialog
            show={showSkillSlider}
            selected={course.skills || []}
            onFinish={handleAddSkillsFinish}
            onClose={() => setShowSkillSlider(false)}
          />
        )}

        <PublishView
          isOpen={isPublished}
          onClose={() => setIsPublished(false)}
          learners={learners || []}
          handleAssignLearners={() => setShowAddLearner(true)}
          loading={loadingLearners}
          hasMoreItems={hasMoreItems}
          handleLoadMoreLearners={handleMoreLearners}
        />

      </div>

      <AddLearnersDialog
        show={showAddLearner}
        course={course}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />
    </CourseContext.Provider>
  );
};

interface EmptyCourseContentProps {
  course: string;
}

const EmptyCourseContent: React.FC<EmptyCourseContentProps> = ({ course }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.emptyCourseContent}>
      <div className={styles.placeholder}>
        <div className={styles.icon}></div>
        <p className={styles.text}>
          Lessons and assessments you create will appear here
        </p>
      </div>
      <div className={styles.placeholder}>
        <Button
          variant="primary"
          icon="paperflite-right-arrow"
          className={styles.createButton}
          onClick={() =>
            navigate(`/coaching/manage/courses/draft/${course}/contents`)
          }
        >
          Create course structure
        </Button>
      </div>
    </div>
  );
};

interface PublishViewProps {
  isOpen: boolean;
  onClose: () => void;
  learners: any[];
  handleAssignLearners: () => void;
  loading: boolean;
  hasMoreItems: boolean;
  handleLoadMoreLearners: () => void;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  onClose,
  learners,
  handleAssignLearners,
  loading,
  hasMoreItems,
  handleLoadMoreLearners,
}) => {
  const PublishCourseIcon = "/images/coaching/publish.svg";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        dispatch(invalidatePublishCourse());
        onClose();
      }
    },
  });

  const learnersTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 1000,
  });

  useEffect(() => {
    return () => {
      dispatch(invalidatePublishCourse());
    };
  }, []);


  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <EmptyMessage
            width={500}
            image={PublishCourseIcon}
            heading="Congratulations!"
            message="Your course has been published."
          />
          <div className={styles.actions}>
            <Button
              variant="gradient"
              icon="paperflite-right-arrow"
              text="View Course Details"
              onClick={() => setClosePublish(true)}
            />
            <Button
              variant="secondary"
              icon="paperflite-close"
              text="Exit"
              onClick={() => navigate(`/coaching/manage`)}
            />
          </div>

          {learners.length > 0 &&
            learnersTransitions(
              (transitionStyles, item) =>
                item && (
                  <animated.div
                    className={styles.learnersContainer}
                    style={transitionStyles}
                  >
                    <div className={styles.header}>
                      <div className={styles.title}>
                        Here's the list of learners whom you have assigned this
                        course to,
                      </div>
                      <Button
                        variant="tertiary"
                        icon="paperflite-user-settings"
                        text="Assign learners"
                        className={styles.assign}
                        onClick={handleAssignLearners}
                      />
                    </div>

                    <InfiniteScroll
                      className={styles.learners}
                      loading={loading}
                      hasMore={hasMoreItems}
                      loadMore={handleLoadMoreLearners}
                      loader={() => <LearnerPlaceholder />}
                    >
                      {learners.map((learner, index) => {
                        const firstName = learner?.user?.firstName || "";
                        const lastName = learner?.user?.lastName || "";
                        const fullName = `${firstName} ${lastName}`.trim() || "";

                        return (
                          <Col md={4} key={index} className={styles.learner}>
                            <Thumbnail
                              media={learner?.user?.icon}
                              defaultImage="/images/assets/default_asset.jpg"
                              className={styles.icon}
                            />

                            <Text className={styles.name} text={fullName} lines={1} />
                          </Col>
                        );
                      })}
                    </InfiniteScroll>
                  </animated.div>
                ),
            )}
        </animated.div>
      ),
  );
};


export const LearnerPlaceholder: React.FC<{}> = ({ }) => {
  return (
    <div className={styles.learnerPlaceholder}>
      <div className={styles.icon}></div>
      <div className={cx(styles.placeholder)}>
        <div className={cx(styles.name, styles.block)}></div>
      </div>
    </div>
  );
}